import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Download, Search, Filter, RefreshCw,
  ArrowLeft, ChevronDown, ChevronRight, UserCog
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StaffByRole() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, role_groups: [] });
  const [expandedRoles, setExpandedRoles] = useState({});
  const [filterRole, setFilterRole] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/roles/staff-by-role`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
        // Expand first role by default
        if (result.role_groups?.length > 0) {
          setExpandedRoles({ [result.role_groups[0].role]: true });
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (role) => {
    setExpandedRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const exportToCSV = () => {
    const rows = [['Role', 'Staff Name', 'Employee ID', 'Designation', 'Department', 'Phone']];
    
    data.role_groups.forEach(group => {
      group.staff.forEach(staff => {
        rows.push([
          group.role_name,
          staff.full_name,
          staff.employee_id,
          staff.designation || '',
          staff.department || '',
          staff.phone || ''
        ]);
      });
    });
    
    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff_by_role_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'Admin': 'bg-red-500',
      'Accountant': 'bg-green-500',
      'HR Manager': 'bg-blue-500',
      'Teacher': 'bg-purple-500',
      'Librarian': 'bg-yellow-500',
      'Front Desk': 'bg-pink-500',
      'Exam Controller': 'bg-orange-500',
      'Admission Officer': 'bg-cyan-500',
      'Inventory Manager': 'bg-teal-500',
      'Hostel In-charge': 'bg-indigo-500',
      'Transport Manager': 'bg-lime-500',
      'Unassigned': 'bg-gray-400'
    };
    return colors[role] || 'bg-gray-500';
  };

  const filteredGroups = filterRole 
    ? data.role_groups.filter(g => g.role === filterRole)
    : data.role_groups;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Staff by Role</h1>
            <p className="text-gray-600 mt-1">View staff members grouped by their roles</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-800">{data.summary?.total_staff || 0}</p>
              <p className="text-sm text-gray-500">Total Staff</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{data.summary?.assigned_staff || 0}</p>
              <p className="text-sm text-gray-500">With Roles</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">{data.summary?.unassigned_staff || 0}</p>
              <p className="text-sm text-gray-500">No Role</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{data.summary?.total_roles || 0}</p>
              <p className="text-sm text-gray-500">Active Roles</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All Roles</option>
              {data.role_groups?.map((group) => (
                <option key={group.role} value={group.role}>
                  {group.role_name} ({group.staff_count})
                </option>
              ))}
            </select>
            {filterRole && (
              <Button variant="outline" size="sm" onClick={() => setFilterRole('')}>
                Clear Filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Role Groups */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <Card key={group.role} className="overflow-hidden">
              <div
                onClick={() => toggleRole(group.role)}
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${getRoleBadgeColor(group.role)}`}></div>
                  <div>
                    <h3 className="font-bold text-lg">{group.role_name}</h3>
                    <p className="text-sm text-gray-500">{group.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium">
                    {group.staff_count} staff
                  </span>
                  {expandedRoles[group.role] ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {expandedRoles[group.role] && (
                <CardContent className="border-t bg-gray-50">
                  {group.staff.length === 0 ? (
                    <p className="text-center py-4 text-gray-500">No staff in this role</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-sm text-gray-500">
                            <th className="p-3">Name</th>
                            <th className="p-3">Employee ID</th>
                            <th className="p-3">Designation</th>
                            <th className="p-3">Department</th>
                            <th className="p-3">Phone</th>
                            <th className="p-3">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.staff.map((staff) => (
                            <tr key={staff.id} className="border-t hover:bg-white">
                              <td className="p-3 font-medium">{staff.full_name}</td>
                              <td className="p-3 text-gray-600">{staff.employee_id}</td>
                              <td className="p-3 text-gray-600">{staff.designation || '-'}</td>
                              <td className="p-3 text-gray-600">{staff.department || '-'}</td>
                              <td className="p-3 text-gray-600">{staff.phone || '-'}</td>
                              <td className="p-3">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate('/roles/assign');
                                  }}
                                >
                                  <UserCog className="h-3 w-3 mr-1" /> Edit
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
